import { useEffect, type ReactNode } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthStore } from '../auth/authStore';
import { CartSummary, type CartSummaryModel } from '../components/booking/CartSummary';
import { BookingFlowResumer, useBookingFlowResume, type UrlResume } from './BookingFlowResumer';
import { BookingFlowStepper } from './BookingFlowStepper';
import { useBookingFlowContext } from './BookingFlowProvider';
import { useSentForApproval } from '../components/booking/useSentForApproval';
import { useBookingDeadline } from './useBookingDeadline';
import { DeadlineChip, DeadlineExpiredDialog } from './DeadlineExpiredDialog';
import type { BookingFlow } from './useBookingFlow';
import type { PersistedBookingFlowConfig, ResumeAdapter } from './resume';

/** `renderStep` ve `approvalGate.render` handler'larına verilen bağlam. `runStep`
 *  paylaşılan `hata → snackbar` sarımıyla gelir; ham promise için `flow.runStep`. */
export interface BookingStepContext<TState> {
  flow: BookingFlow<TState>;
  running: boolean;
  runStep: (patch?: Partial<TState>) => void;
}

export interface BookingFlowShellConfig<TState> {
  title: string;
  description?: ReactNode;
  flowConfig: PersistedBookingFlowConfig<TState>;
  resumeAdapter: ResumeAdapter<TState>;
  urlResume?: UrlResume;
  /** step id → o adımın gövdesini render eden fonksiyon. */
  renderStep: Record<string, (ctx: BookingStepContext<TState>) => ReactNode>;
  /** Kurumsal onay kapısı — aktifse `passengerStepId` adımında `render` çağrılır. */
  approvalGate?: {
    passengerStepId: string;
    isActive: (
      state: TState,
      sessionEmployeeId: number | null
    ) => { employeeId: number; maxBudget?: number | null } | null;
    render: (
      ctx: BookingStepContext<TState> & { employeeId: number; maxBudget?: number | null }
    ) => ReactNode;
  };
  /** "Onaya gönderildi" sonrası akıştan çıkış. */
  sentForApproval?: {
    isSent: (state: TState) => boolean;
    passengerStepId: string;
    reservationPath: string;
    message: string;
  };
  /** Adımlar boyunca gösterilecek kalıcı fiyat özeti. */
  cartSummary?: (state: TState) => CartSummaryModel | null;
  /** Süre geri sayımı (uçak: arama sonrası 15 dk / resume `expireAt`). */
  deadline?: {
    getDeadlineMs: (state: TState, resumeDeadlineMs: number | null) => number | null;
    isPaymentDone: (state: TState) => boolean;
    tabTitlePrefix?: string;
    expiredCopy?: { resumed: string; fresh: string };
  };
  /** Akış tamamlandığında (son adım + idle + ödeme/finalize) çağrılır — ör. uçakta
   *  bilet detay sayfasına yönlendirme. Çağrılmadan önce `flow.reset()` YAPILMAZ; handler
   *  gerekiyorsa kendisi çağırır. */
  onComplete?: (ctx: BookingStepContext<TState>) => void;
}

function ShellInner<TState>({ config }: { config: BookingFlowShellConfig<TState> }) {
  const flow = useBookingFlowContext<TState>();
  const { enqueueSnackbar } = useSnackbar();
  const sessionEmployeeId = useAuthStore((s) => s.employeeId);
  const { resumed, deadlineMs: resumeDeadlineMs } = useBookingFlowResume();
  const running = flow.status === 'running';

  const runStep = (patch?: Partial<TState>) => {
    flow.runStep(patch).catch((err) => {
      enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' });
    });
  };
  const ctx: BookingStepContext<TState> = { flow, running, runStep };

  const deadlineMs = config.deadline
    ? config.deadline.getDeadlineMs(flow.state, resumeDeadlineMs)
    : null;
  const paymentDone = config.deadline ? config.deadline.isPaymentDone(flow.state) : false;
  const {
    remainingMs,
    expired,
    redirectIn,
    active: deadlineActive,
  } = useBookingDeadline({
    deadlineMs,
    paymentDone,
    tabTitlePrefix: config.deadline?.tabTitlePrefix,
  });

  const complete =
    !!config.onComplete && flow.isLastStep && flow.status === 'idle' && config.deadline?.isPaymentDone(flow.state);
  useEffect(() => {
    if (complete) config.onComplete?.(ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  useSentForApproval({
    sent: config.sentForApproval ? config.sentForApproval.isSent(flow.state) : false,
    idle: flow.status === 'idle',
    currentStepId: flow.currentStep?.id,
    passengerStepId: config.sentForApproval?.passengerStepId ?? '',
    reservationPath: config.sentForApproval?.reservationPath ?? '/',
    reset: flow.reset,
    message: config.sentForApproval?.message ?? '',
  });

  const stepId = flow.currentStep?.id;

  let body: ReactNode = null;
  const gate = config.approvalGate;
  if (gate && stepId === gate.passengerStepId) {
    const active = gate.isActive(flow.state, sessionEmployeeId);
    body = active ? gate.render({ ...ctx, ...active }) : config.renderStep[stepId]?.(ctx) ?? null;
  } else if (stepId) {
    body = config.renderStep[stepId]?.(ctx) ?? null;
  }

  const cart = config.cartSummary?.(flow.state) ?? null;

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {config.deadline && (
          <DeadlineExpiredDialog
            open={expired}
            redirectIn={redirectIn}
            resumed={resumed}
            copy={config.deadline.expiredCopy}
          />
        )}
        <BookingFlowStepper rightSlot={deadlineActive ? <DeadlineChip remainingMs={remainingMs} /> : undefined} />
        {flow.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {flow.error}
          </Alert>
        )}
        {body}
      </Box>
      {cart && (
        <Box sx={{ width: 300, flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
          <CartSummary model={cart} sticky />
        </Box>
      )}
    </Box>
  );
}

/** Booking akışı kabuğu — başlık + resumer + stepper + hata + adım gövdesi + onay
 *  kapısı + "onaya gönderildi" çıkışı + opsiyonel fiyat özeti. Ürün farkları
 *  `config.renderStep` handler'larında (bugünkü adım JSX'i buraya taşınır). */
export function BookingFlowShell<TState>({ config }: { config: BookingFlowShellConfig<TState> }) {
  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        {config.title}
      </Typography>
      {config.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {config.description}
        </Typography>
      )}
      <BookingFlowResumer
        config={config.flowConfig}
        adapter={config.resumeAdapter}
        urlResume={config.urlResume}
      >
        <ShellInner config={config} />
      </BookingFlowResumer>
    </Box>
  );
}
